<template>
  <div v-if="tip" class="tip-card">
    <!-- Tip Header -->
    <div class="tip-header">
      <a :href="`/${tipAuthor.username}`">
        <img :src="tipAuthor.avatar" alt="Tip Author Profile" class="profile-pic" />
      </a>
      <div class="tip-details">
        <p class="author">
          {{ tipAuthor.username }}
          <img
            v-if="tipAuthor.verified"
            src="@/assets/verified.png"
            alt="Verified"
            class="verified-badge"
            title="Verified Pro"
          />
          <router-link
            v-if="tipAuthor.ski_schools && tipAuthor.ski_schools.length > 0"
            :to="`/ski-school/${tipAuthor.ski_schools[0].slug}`"
            class="ski-school-link"
          >
            <img
              :src="tipAuthor.ski_schools[0].logo"
              alt="Ski School"
              class="ski-school-badge"
              :title="`${tipAuthor.ski_schools[0].name} - ${tipAuthor.ski_schools[0].role}`"
            />
          </router-link>
        </p>
        <p class="full-name">{{ tipAuthor.first_name }} {{ tipAuthor.last_name }}</p>
      </div>
      <div class="tip-info">
        <div class="tip-label-date">
          <span class="tip-date">{{ formatDate(tip.created) }}</span>
        </div>
        <span v-if="tip.data.pro_tip" class="pro-tip-badge">PRO TIP</span>
      </div>
    </div>

    <!-- Tip Content -->
    <div class="tip-content">
      <p class="tip-text">{{ tip.data.body }}</p>
    </div>

    <!-- Tip Actions -->
    <div class="tip-actions">
      <div class="upvotes-section">
        <i class="fas fa-arrow-up"></i>
        <span>{{ tip.data.ratecount }} upvotes</span>
      </div>
      <div class="action-group">
        <button class="view-post-button" @click="navigateToPost">
          <i class="fas fa-video"></i>
          <span>View Post</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TipCard',
  props: {
    tip: {
      type: Object,
      required: true
    }
  },
  computed: {
    tipAuthor() {
      return this.tip?.data?.user || {
        username: '',
        avatar: 'default-avatar.png',
        first_name: 'Unknown',
        last_name: ''
      };
    }
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    },
    navigateToPost() {
      this.$router.push(`/post/${this.tip.data.post}`);
    }
  }
};
</script>

<style scoped>
/* General Styles */
body {
  background-color: hsl(210, 34%, 13%);
  color: #d9d9d9;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.tip-card {
  border-radius: 16px;
  margin-bottom: 20px;
  background-color: #192734;
  overflow: hidden;
  transition: background-color 0.3s ease;
  border-left: 4px solid #1da1f2;
}

.tip-card:hover {
  background-color: #22303c;
}

.tip-header {
  display: flex;
  align-items: flex-start;
  text-align: left;
  padding: 12px 16px;
}

@media screen and (max-width: 600px) {
  .tip-header {
    padding: 12px 10px;
  }
}

.tip-header a {
  display: flex;
  align-items: center;
}

.profile-pic {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin-right: 12px;
  border: 2px solid #1da1f2;
  flex-shrink: 0;
}

.tip-details {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.author {
  font-weight: bold;
  color: #ffffff;
  margin: 0;
  font-size: 1em;
  display: flex;
  align-items: center;
  gap: 5px;
}

.full-name {
  font-size: 0.85em;
  color: #8899a6;
  margin: 0;
}


.post-link {
  color: #1da1f2;
  cursor: pointer;
  text-decoration: underline;
}

.post-link:hover {
  color: #5bb3f5;
}

.tip-date {
  color: #8899a6;
}

.tip-info {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-direction: column;
}

.tip-label-date {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.tip-label {
  font-size: 0.75em;
  color: #8899a6;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tip-date {
  font-size: 0.75em;
  color: #8899a6;
}

.pro-tip-badge {
  display: inline-block;
  color: #ffffff;
  background-color: #1da1f2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75em;
  font-weight: bold;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
}

.verified-badge {
  width: 18px;
  height: 18px;
}

.ski-school-badge {
  width: 18px;
  height: 18px;
  border-radius: 2px;
  object-fit: cover;
}

.ski-school-link {
  text-decoration: none;
  color: inherit;
}

.tip-content {
  padding: 0px 16px;
}

@media screen and (max-width: 600px) {
  .tip-content {
    padding: 0px 10px;
  }
}

.tip-text {
  font-size: 1em;
  color: #ffffff;
  text-align: left;
  margin: 0;
  line-height: 1.4;
}

.tip-actions {
  padding: 10px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #8899a6;
  font-size: 0.9em;
}

@media screen and (max-width: 600px) {
  .tip-actions {
    padding: 10px;
  }
}

.upvotes-section {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #ffffff;
}

.upvotes-section i {
  color: #1da1f2;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 15px;
}

.view-post-button {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  color: #ffffff;
  background-color: #1da1f2;
  padding: 8px 16px;
  border-radius: 12px;
  border: none;
  font-family: inherit;
  font-size: inherit;
  transition: background-color 0.2s ease;
}

.view-post-button:hover {
  background-color: #5bb3f5;
}

.view-post-button i {
  transition: transform 0.2s ease;
}

.view-post-button:hover i {
  transform: scale(1.1);
}
</style> 